import { CDC_ADULT_VACCINES } from '@seen/core/cdc/vaccines/adult-vaccines'
import {
  CVX_SYSTEM,
  CVX_TO_VACCINE_GROUP,
  getVaccineFamilyFromDisplay,
  type VaccineFamily,
} from '@seen/core/cdc/vaccines/cvx-mapping'
import { formatDateNumber } from '@seen/core/dates'
import type { ExtendedImmunization } from '@seen/core/models/immunizations/ExtendedImmunization'
import type { ExtendedImmunizationRecommendation } from '@seen/core/models/immunizations/ExtendedImmunizationRecommendation'
import { Badge, Button } from '@seen/ui'
import { IconPlus } from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { orderBy } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'

interface ImmunizationDoseViewProps {
  immunizations: ExtendedImmunization[]
  immunizationRecommendations: ExtendedImmunizationRecommendation[] | undefined
  participantId: string
  onNavigate: (immunizationId: string) => void
  onAddClick: (vaccineCode?: { code: string; display: string }) => void
}

type DoseTableRow = {
  vaccineKey: string
  display: string
  code: string
  system: string
  cvxDisplay?: string
  byDose: Record<number, ExtendedImmunization[]>
  recommendation: ExtendedImmunizationRecommendation | undefined
}

function getVaccineFamily(imm: ExtendedImmunization): VaccineFamily | null {
  // First, try CVX code lookup (most reliable)
  for (const coding of imm.vaccineCode?.coding ?? []) {
    if (coding.system === CVX_SYSTEM && coding.code) {
      const cvxInfo = CVX_TO_VACCINE_GROUP[coding.code]
      if (cvxInfo) {
        return cvxInfo.family
      }
    }
  }

  // Fallback to string matching on display name
  return getVaccineFamilyFromDisplay(imm.vaccineDisplay)
}

export function ImmunizationDoseView({
  immunizations,
  immunizationRecommendations,
  onNavigate,
  onAddClick,
}: ImmunizationDoseViewProps): JSX.Element {
  const doseTableData = useMemo(() => {
    const familyToImmunizations = new Map<
      VaccineFamily,
      ExtendedImmunization[]
    >()
    const ungroupedImmunizations: ExtendedImmunization[] = []

    for (const imm of immunizations) {
      const family = getVaccineFamily(imm)
      if (family) {
        if (!familyToImmunizations.has(family)) {
          familyToImmunizations.set(family, [])
        }
        familyToImmunizations.get(family)?.push(imm)
      } else {
        // Immunizations without a recognized family (rare edge case)
        ungroupedImmunizations.push(imm)
      }
    }

    const vaccineRows: DoseTableRow[] = []

    for (const [family, immunizations] of familyToImmunizations.entries()) {
      // Collect all unique vaccine codes in this group
      const allCodes = new Set<string>()
      const codeDetails = new Map<
        string,
        { system: string; code: string; display: string }
      >()

      for (const imm of immunizations) {
        for (const coding of imm.vaccineCode?.coding ?? []) {
          if (coding.system && coding.code) {
            const key = `${coding.system}|${coding.code}`
            allCodes.add(key)
            if (!codeDetails.has(key)) {
              codeDetails.set(key, {
                system: coding.system,
                code: coding.code,
                display: coding.display ?? '',
              })
            }
          }
        }
      }

      // Use vaccine family as display name and key
      const display = family
      const vaccineKey = family
      const firstCode = Array.from(codeDetails.values())[0]

      // Group by dose number (sort by date if no dose number)
      const byDose: Record<number, ExtendedImmunization[]> = {}
      const withoutDose: ExtendedImmunization[] = []

      for (const imm of immunizations) {
        const doseNum = imm.doseNumber
        if (doseNum !== undefined && doseNum > 0) {
          byDose[doseNum] ??= []
          byDose[doseNum].push(imm)
        } else {
          withoutDose.push(imm)
        }
      }

      // For immunizations without dose numbers, assign them sequentially
      const sortedWithoutDose = orderBy(
        withoutDose,
        (immunization) => immunization.administeredDate ?? '',
        ['asc']
      )

      let nextDose = 1
      for (const imm of sortedWithoutDose) {
        while (byDose[nextDose]) {
          nextDose++
        }
        byDose[nextDose] = [imm]
        nextDose++
      }

      // Find recommendation for this vaccine family
      const recommendation = immunizationRecommendations?.find((rec) => {
        return rec.vaccineCodes?.some((vc) =>
          vc.coding?.some((recCoding) => {
            // Check CVX code match first
            if (recCoding.system === CVX_SYSTEM && recCoding.code) {
              const cvxInfo = CVX_TO_VACCINE_GROUP[recCoding.code]
              if (cvxInfo?.family === family) {
                return true
              }
            }
            // Fallback to display name matching
            const recFamily = getVaccineFamilyFromDisplay(recCoding.display)
            return recFamily === family
          })
        )
      })

      vaccineRows.push({
        vaccineKey,
        display,
        code: firstCode?.code ?? '',
        system: firstCode?.system ?? '',
        cvxDisplay: firstCode?.display,
        byDose,
        recommendation,
      })
    }

    // Handle ungrouped immunizations (rare edge case - each gets its own row)
    for (const imm of ungroupedImmunizations) {
      const byDose: Record<number, ExtendedImmunization[]> = {}
      const doseNum = imm.doseNumber
      if (doseNum !== undefined && doseNum > 0) {
        byDose[doseNum] = [imm]
      } else {
        byDose[1] = [imm]
      }

      const firstCoding = imm.vaccineCode?.coding?.[0]
      vaccineRows.push({
        vaccineKey: firstCoding?.code ?? imm.id,
        display: imm.vaccineDisplay ?? 'Unknown Vaccine',
        code: firstCoding?.code ?? '',
        system: firstCoding?.system ?? '',
        cvxDisplay: firstCoding?.display,
        byDose,
        recommendation: undefined,
      })
    }

    // Add rows for CDC adult vaccines that don't have any immunizations yet
    const existingFamilies = new Set(vaccineRows.map((row) => row.vaccineKey))

    for (const cdcVaccine of CDC_ADULT_VACCINES) {
      if (!existingFamilies.has(cdcVaccine.family)) {
        // Find recommendation for this vaccine family
        const recommendation = immunizationRecommendations?.find((rec) => {
          return rec.vaccineCodes?.some((vc) =>
            vc.coding?.some((recCoding) => {
              // Check CVX code match first (check all CVX codes for this family)
              if (recCoding.system === CVX_SYSTEM && recCoding.code) {
                return cdcVaccine.cvxCodes.includes(recCoding.code)
              }
              // Fallback to display name matching
              const family = getVaccineFamilyFromDisplay(recCoding.display)
              return family === cdcVaccine.family
            })
          )
        })

        // Use first active CVX code as representative code (prefer active over all codes)
        const firstCvxCode =
          cdcVaccine.activeCvxCodes[0] ?? cdcVaccine.cvxCodes[0]
        const cvxInfo = firstCvxCode
          ? CVX_TO_VACCINE_GROUP[firstCvxCode]
          : undefined

        vaccineRows.push({
          vaccineKey: cdcVaccine.family,
          display: cdcVaccine.display,
          code: firstCvxCode ?? '',
          system: cdcVaccine.system,
          cvxDisplay: cvxInfo?.display ?? cdcVaccine.display,
          byDose: {},
          recommendation,
        })
      }
    }

    return vaccineRows.sort((a, b) => a.display.localeCompare(b.display))
  }, [immunizations, immunizationRecommendations])

  const maxDose = useMemo(() => {
    let max = 0
    for (const row of doseTableData) {
      const doses = Object.keys(row.byDose).map(Number)
      max = Math.max(max, ...doses)
    }
    return (max || 2) + 1 // +1 to make room for recommendation
  }, [doseTableData])

  const columns = useMemo<ColumnDef<DoseTableRow>[]>(() => {
    const cols: ColumnDef<DoseTableRow>[] = [
      {
        id: 'vaccine',
        header: 'Vaccine',
        accessorFn: (row) => row.display,
        cell: ({ row }) => (
          <div className="font-medium">{row.original.display}</div>
        ),
      },
    ]

    for (let i = 1; i <= maxDose; i++) {
      cols.push({
        id: `dose${i}`,
        header: `Dose ${i}`,
        accessorFn: (row) => row.byDose[i]?.[0]?.administeredDate,
        cell: ({ row }) => {
          const immunizations = row.original.byDose[i]
          if (!immunizations || immunizations.length === 0) {
            // Check if this is the next dose slot and we have a recommendation
            const existingDoses = Object.keys(row.original.byDose).map(Number)
            const nextDose =
              existingDoses.length > 0 ? Math.max(...existingDoses) + 1 : 1
            const isNextDose = i === nextDose
            const recommendation = row.original.recommendation

            if (isNextDose && recommendation) {
              const dateDue = recommendation.firstNonCompletedDateDue
              const dateEarliest = recommendation.firstNonCompletedDateEarliest

              if (dateDue || dateEarliest) {
                const hasBothDates = dateDue && dateEarliest
                const content = (
                  <Badge variant="secondary" className="font-medium">
                    {dateDue ? (
                      <span>Due: {formatDateNumber(dateDue)}</span>
                    ) : dateEarliest ? (
                      <span>Earliest: {formatDateNumber(dateEarliest)}</span>
                    ) : null}
                  </Badge>
                )

                if (hasBothDates) {
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>{content}</TooltipTrigger>
                      <TooltipContent>
                        <div className="flex flex-col text-xs">
                          {dateDue && (
                            <span>Due: {formatDateNumber(dateDue)}</span>
                          )}
                          {dateEarliest && (
                            <span>
                              Earliest: {formatDateNumber(dateEarliest)}
                            </span>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )
                }

                return content
              }
            }

            return ''
          }

          return (
            <div className="flex flex-col items-start gap-1">
              {immunizations.map((imm) => {
                const isRefused = imm.isNotDone && imm.statusReason
                const refusalReason = imm.refusalReason

                if (isRefused) {
                  return (
                    <Tooltip key={imm.id}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="link"
                          onClick={(e) => {
                            e.stopPropagation()
                            onNavigate(imm.id)
                          }}
                          className="h-fit p-0 text-destructive"
                        >
                          Declined
                          {imm.administeredDate
                            ? ` ${formatDateNumber(imm.administeredDate)}`
                            : ''}
                        </Button>
                      </TooltipTrigger>
                      {refusalReason && (
                        <TooltipContent>
                          <div className="text-xs">{refusalReason}</div>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  )
                }

                return (
                  <Button
                    key={imm.id}
                    variant="link"
                    className="h-fit"
                    onClick={(e) => {
                      e.stopPropagation()
                      onNavigate(imm.id)
                    }}
                  >
                    {imm.administeredDate
                      ? formatDateNumber(imm.administeredDate)
                      : ''}
                  </Button>
                )
              })}
            </div>
          )
        },
      })
    }

    cols.push({
      id: 'add',
      header: '',
      accessorFn: (row) => (row.recommendation ? 'yes' : 'no'),
      cell: ({ row }) => {
        return (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              if (row.original.code) {
                onAddClick({
                  code: row.original.code,
                  display: row.original.cvxDisplay ?? row.original.display,
                })
              } else {
                onAddClick()
              }
            }}
            className="h-fit gap-1"
          >
            <IconPlus className="size-3" />
            Add
          </Button>
        )
      },
    })

    return cols
  }, [maxDose, onNavigate, onAddClick])

  return <DataTable columns={columns} data={doseTableData} />
}
